// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0

import { AnyLayer } from "../unions";
import { ChildrenCollection } from "./ChildrenCollection";
import { RecursiveRetrievalOptions } from "./LayerRetrievalOptions";

/** @internal */
export interface RecursiveLayerOptions<LayerType> extends RecursiveRetrievalOptions {
    /**
     * Starting point(s) of the recursion.
     * The layers in this collections and their all of their children (recursively) will be included in the result.
     */
    from: ChildrenCollection<LayerType>;

    /**
     * If set to `true`, layers will be ordered by their display order:
     * Layers listed first in the returned array are shown _below_ layers listed at a later index.
     *
     * By default, layers are returned in arbitrary order.
     */
    sortByDisplayOrder?: boolean;
}

export function getRecursiveLayers<LayerType extends AnyLayer>(
    options: RecursiveLayerOptions<LayerType>
): AnyLayer[] {
    const filter = options.filter ?? (() => true);
    const sortByDisplayOrder = options.sortByDisplayOrder ?? false;
    const includeInternalLayers = options.includeInternalLayers ?? false;
    const result: AnyLayer[] = [];
    gatherRecursiveLayers({
        from: options.from,
        filter,
        sortByDisplayOrder,
        includeInternalLayers,
        result
    });
    return result;
}

interface GatherOptions<LayerType extends AnyLayer> {
    from: ChildrenCollection<LayerType>;
    filter: (layer: AnyLayer) => boolean;
    sortByDisplayOrder: boolean;
    includeInternalLayers: boolean;
    result: AnyLayer[];
}

// Walks the layer tree recursively and gathers matching layers into the result array.
function gatherRecursiveLayers<LayerType extends AnyLayer>(
    options: GatherOptions<LayerType>
): void {
    const { from, filter, sortByDisplayOrder, includeInternalLayers, result } = options;
    const layers = from.getItems({
        sortByDisplayOrder: sortByDisplayOrder,
        includeInternalLayers: includeInternalLayers
    });
    for (const layer of layers) {
        if (!filter(layer)) {
            continue;
        } else if (!includeInternalLayers && layer.internal) {
            continue;
        }

        const children = layer.children;
        if (children) {
            // Pushes into result as a side effect
            gatherRecursiveLayers({
                from: children,
                filter,
                sortByDisplayOrder,
                includeInternalLayers,
                result
            });
        }
        result.push(layer);
    }
}
